import { useEffect, useState, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { GridFooterContainer, GridNoRowsOverlay,GridPagination } from "@mui/x-data-grid";
import ExportCSV from "../ExportCsv/ExportCSV";
import { ResizableDataGrid } from "./ResizableDataGrid";

const CustomFooter = (props) => {
  const { title, data } = props;
  return (
    <GridFooterContainer>
      <div style={{ marginLeft: '10px' }}>
        {title}: {data}
      </div>
      <GridPagination {...props} />
    </GridFooterContainer>
  );
};

export const BaseTable = ({
  rows,
  columns,
  rowOrCellClickRedirect,
  pagination = true,
  pageSize = 10,
  height,
  sx,
  rowsPerPageOptions = [10, 20, 30],
  showExportCsvBtn,
  csvMorphemes,
  csvColumns,
  sortModel,
  onSortModelChange,
  isReadOnly,
  onPageSizeChange,
  customFooterData = {},
  totalRows,
  serverRowFetcher,
  paginationModel,
  disableColumnResize = false,
  minColumnWidth = 80,
  loading,
  onSelectionChange, // <-- NEW optional callback
  enableSelection, // <-- NEW prop to toggle selection
  ...restProps
}) => {
  const [pagesize, setPageSize] = useState(pageSize);
  const [selectionModel, setSelectionModel] = useState([]);
  
  // Memoize table columns for better performance
  const tableColumns = useMemo(() => {
    const filteredColumns = !isReadOnly ? columns : columns.filter(({field}) => field.toLowerCase() !== "actions");
    return filteredColumns.map(col => ({
      ...col,
      resizable: col.resizable !== false && !disableColumnResize,
      minWidth: col.minWidth || minColumnWidth,
      // Optimize initial width calculation
      width: col.width || (col.flex ? Math.max(150, minColumnWidth) : undefined),
      // Add performance optimizations for render cells
      ...(col.renderCell && {
        renderCell: (params) => {
          // Wrap render cell in error boundary concept
          try {
            return col.renderCell(params);
          } catch (error) {
            console.error(`Error rendering cell for column ${col.field}:`, error);
            return null;
          }
        }
      })
    }));
  }, [columns, isReadOnly, disableColumnResize, minColumnWidth]);

  // Optimize row click handler
  const rowClickHandler = useCallback((data, event) => {
    event.stopPropagation();
    const correspondingColumnName = data.field;
    
    // Don't redirect if it's the checkbox column (when selection is enabled)
    const isCheckboxColumn = enableSelection && correspondingColumnName === '__check__';
    
    if (correspondingColumnName && 
        correspondingColumnName.toLowerCase() !== "actions" && 
        !isCheckboxColumn) {
      if (rowOrCellClickRedirect) {
        rowOrCellClickRedirect(data);
      }
    }
  }, [rowOrCellClickRedirect, enableSelection]);

  // Handle row clicks for navigation when selection is enabled
  const handleRowClick = useCallback((params, event) => {
    // Only navigate if not clicking on checkbox or actions
    const isCheckbox = event.target.closest('.MuiCheckbox-root');
    const isAction = event.target.closest('[data-field="Actions"]');
    
    if (!isCheckbox && !isAction && rowOrCellClickRedirect) {
      rowOrCellClickRedirect(params);
    }
  }, [rowOrCellClickRedirect]);

  // Throttled column resize handler for better performance
  const handleColumnResize = useCallback((columnField, newWidth) => {
    // Debounce logging to avoid spam
    if (process.env.NODE_ENV === 'development') {
      console.log(`Column ${columnField} resized to ${newWidth}px`);
    }
  }, []);

  useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize, rows]);

  // Memoize component props for better performance
  const componentsConfig = useMemo(() => ({
    NoRowsOverlay: GridNoRowsOverlay,
    Footer: CustomFooter
  }), []);

  const componentsPropsConfig = useMemo(() => ({
    footer: customFooterData
  }), [customFooterData]);

  // Enhanced styles for smoother interactions
  const enhancedSx = useMemo(() => ({
    // Performance optimizations
    '& .MuiDataGrid-main': {
      overflow: 'hidden'
    },
    '& .MuiDataGrid-virtualScroller': {
      // Enable hardware acceleration for smoother scrolling
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      // Remove any top margin/padding that causes empty space
      marginTop: '0 !important',
      paddingTop: '0 !important'
    },
    // Remove any unwanted spacing/padding
    '& .MuiDataGrid-root': {
      border: 'none',
      '& .MuiDataGrid-main': {
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          marginBottom: '0 !important'
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          height: 'auto !important',
          paddingTop: '0 !important',
          marginTop: '0 !important'
        },
        '& .MuiDataGrid-virtualScrollerRenderZone': {
          transform: 'translate3d(0px, 0px, 0px) !important',
          paddingTop: '0 !important',
          marginTop: '0 !important'
        }
      }
    },
    // Remove spacing from data container
    '& .MuiDataGrid-dataContainer': {
      paddingTop: '0 !important',
      marginTop: '0 !important'
    },
    // Smooth hover transitions
    '& .MuiDataGrid-row': {
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)'
      },
      // Ensure no extra spacing
      margin: '0 !important',
      padding: 0,
      '&:first-of-type': {
        marginTop: '0 !important',
        paddingTop: '0 !important'
      }
    },
    '& .MuiDataGrid-cell': {
      transition: 'border-color 0.15s ease',
      padding: '8px 16px'
    },
    // Better focus indicators
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: '2px solid rgba(25, 118, 210, 0.3)',
      outlineOffset: '-2px'
    },
    // Ensure container takes full space
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...sx
  }), [sx]);

    // handle selection only if enabled
  const handleSelectionChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      if (enableSelection && onSelectionChange) {
        const selectedRows = rows.filter((row) => newSelection.includes(row.id));
        onSelectionChange(selectedRows);
      }
    },
    [rows, onSelectionChange, enableSelection]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {showExportCsvBtn && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <ExportCSV rows={rows || []} columns={csvColumns} csvMorphemes={csvMorphemes} />
        </Box>
      )}
      <Box sx={{ height: "100%", width: "100%" }}>
        <ResizableDataGrid
          rows={rows || []}
          columns={tableColumns}
          {...(paginationModel && {page: paginationModel.page})}
          pageSize={pagesize}
          loading={loading}
          // only enable selection if requested
          {...(enableSelection && {
            checkboxSelection: true,
            disableRowSelectionOnClick: false,
            selectionModel: selectionModel,
            onSelectionModelChange: handleSelectionChange,
          })}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            if (onPageSizeChange) onPageSizeChange(newPageSize);
          }}
          {...(serverRowFetcher && {paginationMode: "server"})}
          rowCount={totalRows}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination={pagination}
          {...(!enableSelection && { disableSelectionOnClick: true })}
          onColumnResize={handleColumnResize}
          components={componentsConfig}
          componentsProps={componentsPropsConfig}
          autoHeight={true}
          {...(!enableSelection && { onCellClick: rowClickHandler })}
          {...(pagination && { pageSize: pagesize })}
          {...(pagination && { rowsPerPageOptions: rowsPerPageOptions })}
          {...(!pagination && { hideFooter: true })}
          {...(sortModel && { sortModel })}
          {...(onSortModelChange && { onSortModelChange })}
          sx={enhancedSx}
          // Performance optimizations
          density="standard"
          disableVirtualization={false}
          {...restProps}
        />
      </Box>
    </Box>
  );
};